import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import Popup from "reactjs-popup";
import 'reactjs-popup/dist/index.css';
import './SubUserAgreementPopup.css';

import { CaptureUserApproval } from "../../Apis/CaptureUserApproval";
import { CaptureAdminApproval } from "../../Apis/CaptureAdminApproval";
import Toaster from "../Toaster/Toaster";
import Loader from "../../Components/Loader/Loader";

import {
  handleApiError,
  showNotification
} from "../../actions/notificationActions";

function SubUserAgreementPopup({ inviteDetails, setInvitePopup }) {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [acknowledgeCheck, setAcknowledgeCheck] = useState(true);
  const [approvalSubmit, setApprovalSubmit] = useState(false);
  const [userDecision, setUserDecision] = useState(null);

  const inviteToken = sessionStorage.getItem("invite_token");

  const isSubUser = inviteDetails.token_type === 'Subuser' ? true : false;
  const companyName = inviteDetails?.company_name || inviteDetails?.new_company_name || 'the company';
  const adminName = inviteDetails?.admin_name || inviteDetails?.old_admin_name || 'the admin';

  const handleClose = () => {
    setApprovalSubmit(false);
    sessionStorage.removeItem("invite_token");
    setInvitePopup(false);
  };

  const submitApproval = async (user_action) => {
    const apiCall = isSubUser ? CaptureUserApproval : CaptureAdminApproval;
    const label = isSubUser ? 'Advisor User' : 'Admin';

    try {
      setIsLoading(true);
      const res = await apiCall(inviteToken, user_action);
      if (res.status === 200) {
        if (res.result.status === 404) {
          dispatch(showNotification(res.result.message, "info", "toaster"));
        } else {
          setUserDecision(user_action);
          dispatch(showNotification(`${label} Decision captured successfully`, "success", "toaster"));
          setApprovalSubmit(true);
        }
      } else {
        handleApiError(res, dispatch);
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      setIsLoading(false);
    }
  };

  const PopupHeader = () => (
    <div className="popup-head">
      <img src="Assets/adaptive-name.png" alt="Adaptive Name" />
    </div>
  );

  const SuccessMessage = ({ accepted }) => (
    <div className="popup-content success-popup">
      <div className="success-icon">
        <img src={`Assets/shield-${accepted ? 'green' : 'red'}.png`} alt={accepted ? "Success" : "Declined"} />
      </div>
      <h2>{accepted ? (isSubUser ? 'Submission Successful!' : 'Transfer Approved!') : (isSubUser ? 'Invitation Declined' : 'Transfer Declined')}</h2>

      {accepted ? (
        isSubUser ? (
          <>
            <h3>Thank you for confirming your participation in <strong>{companyName}'s Advisor Firm Plan</strong>.</h3>
            <h3>
              You now have access to unlimited portfolio management capabilities and cutting-edge tools to stay ahead of the market.
            </h3>
            <div className="success-highlights">
              <ul>
                <li><strong>Equity and Options Portfolio Analytics</strong>with Adaptive Intelligence™</li>
                <li><strong>Comprehensive Market Protection</strong> using Market Shield™</li>
                <li><strong>Covered Call Writing</strong> for Income</li>
                <li><strong>Real-Time Market Insights</strong> and Data</li>
                <li><strong>Unlimited Portfolio</strong> and Tickers</li>
                <li><strong>Seamless Data Migration</strong> Migration from your current account (you will use the same login and password)</li>
                <li><strong>Exclusive Monthly Reports</strong> via Option Intelligence™</li>
              </ul>
            </div>
            <h3>We’re excited to support you in this next chapter of your investment journey.</h3>
          </>
        ) : (
            <>
              <h3>You have approved the transfer of <strong>{inviteDetails.sub_user_name}</strong> to <strong>{companyName}</strong>'s Advisor Firm Plan.</h3>
              <p>An invitation has now been sent to the advisor user for their confirmation. The transfer will be completed only after the advisor user accepts the invitation.</p>
              <p>Until then, the advisor user will continue to have access under your firm’s plan.</p>
            </>
        )
      ) : (
        isSubUser ? (
          <>
            <h3>You’ve declined the invitation to join <strong>{companyName}'s Advisor Firm Plan</strong>.</h3>
            <h3>If this was a mistake or you change your mind, please contact <strong>{adminName}</strong> for a new invitation.</h3>
            <h3>Your current subscription will remain unchanged.</h3>
          </>
        ) : (
          <>
            <h3>You have declined the transfer request for <strong>{inviteDetails.sub_user_name}</strong> to join <strong>{companyName}</strong>'s Advisor Firm Plan.</h3>
            <p>The advisor user will remain active under your firm’s current subscription with no changes to their access or account status.</p>
          </>
        )
      )}

      <div className="popup-buttons">
        <button className="decline-btn" onClick={handleClose}>Close</button>
      </div>
    </div>
  );

  const SubUserInvitation = () => (
    <div className="popup-content">
      <h2>Confirm Your Advisor User Invitation to {companyName}'s Advisor Firm Plan</h2>
      <p>Dear Advisor,</p>
      <p>You have received an exclusive invitation from <strong>{adminName}</strong>, the administrator at <strong>{companyName}</strong>, to join the Advisor Firm Plan as an advisor user. This transition will provide you with access to advanced tools, enhanced features, and a comprehensive suite of services designed to optimize your investment strategies and help you maximize your portfolio performance.</p>
      <p>By accepting this invitation, you will be able to tap into the full power of the Advisor Firm Plan, which is specifically designed for professionals who need unlimited portfolio management capabilities and cutting-edge tools to stay ahead of the market.</p>
      <h3>Key Benefits of Joining the Advisor Firm Plan:</h3>
      <ul>
        <li><strong>Equity and Options Portfolio Analytics</strong>with Adaptive Intelligence™</li>
        <li><strong>Comprehensive Market Protection</strong> using Market Shield™</li>
        <li><strong>Covered Call Writing</strong> for Income</li>
        <li><strong>Real-Time Market Insights</strong> and Data</li>
        <li><strong>Unlimited Portfolio</strong> and Tickers</li>
        <li><strong>Seamless Data Migration</strong> Migration from your current account (you will use the same login and password)</li>
        <li><strong>Exclusive Monthly Reports</strong> via Option Intelligence™</li>
      </ul>
      <h3>What Happens After You Approve:</h3>
      <ul>
        <li>By accepting this invitation, you will immediately gain access to all the above features and tools under the Advisor Firm Plan and you accept the terms and conditions of the <a href="https://www.adaptive-investments.com/terms-conditions/"><strong>User Agreement</strong></a> and will gain immediate access to the Advisor Firm Plan.</li>
        <li>If you are a current user (based on the email associated with your account), your data and login will be the same. However, you will grant Adaptive Advisers, LLC full access and ownership of data in your account, including client portfolio data.</li>
        <li>Adaptive Advisers, LLC may disable or delete your account without advanced notice, and you will not be able to resume access or retrieve data in your disabled account.</li>
        <li>Once you accepted the invitation, you will no longer be charged monthly subscription fees of the individual advisor plan, as your firm will be paying a subscriptions at the firm level.</li>
        <li>If you have elected a prepaid annual payment plan, will calculate and refund the portfolio of the fee proportional to the remaining duration of in the current payment cycle. Please give up to 30 days for the refund.</li>
      </ul>
      <h3>Acknowledgment and Acceptance:</h3>
      <p>
        By accepting this invitation, you are agreeing to transition your account to the Advisor Firm Plan and benefit from the enhanced tools, advanced analytics, and exclusive resources offered by the firm.
      </p>
      <label>
        <input type="checkbox" checked={acknowledgeCheck} onChange={(e) => setAcknowledgeCheck(e.target.checked)} />
        I acknowledge that I have reviewed and fully understand the terms and conditions of the Advisor Firm Plan <a href="https://www.adaptive-investments.com/terms-conditions/"><strong>User Agreement</strong></a>, and I agree to transition my account to the firm’s subscription.
      </label>
      <div className="popup-buttons">
        <button disabled={!acknowledgeCheck} className="approve-btn" onClick={() => submitApproval('Accepted')}>Approve</button>
        <button className="decline-btn" onClick={() => submitApproval('Declined')}>Decline</button>
      </div>
    </div>
  );

  const TransferRequest = () => (
    <div className="popup-content">
      <h2>Approval Required: Sub-User Transfer Request</h2>
      <p>Dear <strong>{inviteDetails.old_admin_name}</strong>,</p>
      <p>
        Your advisor user <strong>{inviteDetails.sub_user_name}</strong> (Email: <strong>{inviteDetails.sub_user_email}</strong>) has been invited to join the <strong>Advisor Firm Plan </strong> of <strong>{companyName}</strong>.
      </p>
      <p>
        By approving this request, you are authorizing the complete transfer of the advisor user’s account from your firm to {companyName}. This transfer includes all associated data and records currently linked to the advisor user.
      </p>
      <h3>Please note the following:</h3>
      <ul>
        <li>The advisor user will be removed from your firm’s plan and added to the Advisor Firm Plan under <strong>{companyName}</strong>.</li>
        <li>Your administrative control over this advisor user and their data will be fully revoked.</li>
        <li>The receiving firm will gain full access and administrative rights over the advisor user's account and historical data.</li>
        <li>This action is final and cannot be reversed once approved.</li>
      </ul>
      <h3>Acknowledgment and Authorization:</h3>
      <p>To proceed, please confirm your understanding and approval by selecting the checkbox below.</p>
      <label>
        <input type="checkbox" checked={acknowledgeCheck} onChange={(e) => setAcknowledgeCheck(e.target.checked)} />
         I acknowledge and formally authorize the transfer of the advisor user, <strong>{inviteDetails.sub_user_name}</strong> (Email: <strong>{inviteDetails.sub_user_email}</strong>), including all associated data and account access, to <strong>{companyName}</strong>’s Advisor Firm Plan. I understand that this action will permanently revoke our firm’s access and administrative control over the advisor user’s account.
      </label>
      <div className="popup-buttons">
        <button disabled={!acknowledgeCheck} className="approve-btn" onClick={() => submitApproval('Accepted')}>Approve Transfer</button>
        <button className="decline-btn" onClick={() => submitApproval('Declined')}>Decline Transfer</button>
      </div>
    </div>
  );

  const renderPopupContent = () => {
    if (approvalSubmit) {
      return <SuccessMessage accepted={userDecision === 'Accepted'} />;
    }
    return isSubUser ? <SubUserInvitation /> : <TransferRequest />;
  };

  return (
    <>
      {isLoading && <div className="loading-overlay"><Loader /></div>}
      <div className="toast-container"><Toaster /></div>
      <Popup open modal closeOnDocumentClick={false} closeOnEsc={false}>
        <div className="add-portfolio-popup">
          <PopupHeader />
          {renderPopupContent()}
        </div>
      </Popup>
    </>
  );
}

export default SubUserAgreementPopup;
